/* ==========================================================================
   LIORA — Shopify theme behaviour
   Cart: Shopify AJAX API (/cart.js, /cart/add.js, /cart/change.js); automatic
   discounts (Angebot 2 + 1) come back from Shopify in the cart totals.
   ========================================================================== */

(() => {
  const CFG = window.LIORA || { root: "/", cartUrl: "/cart", singleVariantId: 0, fallbackImage: "" };
  const OFFER = window.LIORA_OFFER || { active: false, lastDayLabel: "" };
  const ROOT = CFG.root.endsWith("/") ? CFG.root : CFG.root + "/";
  const MAX_QTY = 10;
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => [...root.querySelectorAll(sel)];
  const money = (cents) => (cents / 100).toFixed(2).replace(".", ",") + " €";
  const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

  /* ---------- Offer labels ---------- */
  $$("[data-offer-end]").forEach((el) => { if (OFFER.lastDayLabel) el.textContent = OFFER.lastDayLabel; });

  /* ---------- Active navigation item ---------- */
  const normPath = (p) => {
    const clean = p.replace(/\/+$/, "");
    return clean === "" ? "/" : clean;
  };
  const here = normPath(location.pathname);
  $$(".main-nav a:not(.nav-shop), .mobile-menu nav a").forEach((a) => {
    const target = normPath(new URL(a.href, location.href).pathname);
    if (target === here || (target !== "/" && here.startsWith(target + "/"))) {
      a.setAttribute("aria-current", "page");
      a.closest(".nav-drop")?.classList.add("is-current");
    }
  });

  /* ---------- "Hilfe" dropdown ---------- */
  $$(".nav-drop").forEach((drop) => {
    document.addEventListener("click", (e) => { if (!drop.contains(e.target)) drop.open = false; });
    drop.addEventListener("keydown", (e) => { if (e.key === "Escape") { drop.open = false; $("summary", drop).focus(); } });
  });

  /* ---------- Header shadow ---------- */
  const header = $(".site-header");
  const onScroll = () => header && header.classList.toggle("is-scrolled", window.scrollY > 8);
  window.addEventListener("scroll", onScroll, { passive: true });
  onScroll();

  /* ---------- Mobile menu ---------- */
  const menuBtn = $(".menu-toggle");
  const menu = $("#mobile-menu");
  if (menuBtn && menu) {
    const setMenu = (open) => {
      menu.hidden = !open;
      menuBtn.setAttribute("aria-expanded", String(open));
      menuBtn.setAttribute("aria-label", open ? "Menü schließen" : "Menü öffnen");
      document.body.classList.toggle("menu-open", open);
    };
    menuBtn.addEventListener("click", () => setMenu(menu.hidden));
    menu.addEventListener("click", (e) => { if (e.target.closest("a")) setMenu(false); });
    window.addEventListener("keydown", (e) => { if (e.key === "Escape" && !menu.hidden) setMenu(false); });
  }

  /* ---------- Cart (Shopify AJAX API) ---------- */
  let cart = null;
  let busy = false;

  async function api(path, body) {
    const res = await fetch(ROOT + path, {
      method: body ? "POST" : "GET",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: body ? JSON.stringify(body) : undefined,
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  async function refreshCart() {
    try { cart = await api("cart.js"); } catch { cart = cart || { items: [], item_count: 0 }; }
    renderCart();
  }

  async function addItems(items) {
    busy = true;
    try { await api("cart/add.js", { items }); } finally { busy = false; }
    await refreshCart();
  }

  async function changeLine(key, quantity) {
    if (busy) return;
    busy = true;
    try { cart = await api("cart/change.js", { id: key, quantity: Math.max(0, Math.min(MAX_QTY, quantity)) }); }
    catch { /* keep the previous state */ }
    finally { busy = false; }
    renderCart();
  }

  function discountTitles() {
    const titles = new Set();
    (cart.cart_level_discount_applications || []).forEach((d) => titles.add(d.title));
    cart.items.forEach((item) => (item.line_level_discount_allocations || []).forEach((a) => titles.add(a.discount_application.title)));
    return [...titles].filter(Boolean).join(", ") || "Rabatt";
  }

  function lineHtml(item) {
    const img = item.image ? `${item.image}${item.image.includes("?") ? "&" : "?"}width=160` : CFG.fallbackImage;
    // Admin variant titles may carry old marketing ("2 Flaschen (-12 %)"): keep the plain quantity
    const variant = item.variant_title && item.variant_title !== "Default Title" ? item.variant_title.split(" (")[0].split(" –")[0] : "40 ml";
    const discounted = item.final_line_price < item.original_line_price;
    return `
      <li class="cart-line">
        <img src="${esc(img)}" alt="" width="72" height="72" loading="lazy">
        <div class="cart-line-info">
          <span class="cart-line-title">${esc(item.product_title)}</span>
          <span class="cart-line-variant">${esc(variant)}</span>
          <div class="qty" role="group" aria-label="Menge">
            <button type="button" data-line="${esc(item.key)}" data-qty="${item.quantity - 1}" aria-label="Eins weniger">−</button>
            <span aria-live="polite">${item.quantity}</span>
            <button type="button" data-line="${esc(item.key)}" data-qty="${item.quantity + 1}" aria-label="Eins mehr" ${item.quantity >= MAX_QTY ? "disabled" : ""}>+</button>
          </div>
        </div>
        <div class="cart-line-side">
          <span class="cart-line-price">${discounted ? `<s class="small muted">${money(item.original_line_price)}</s><br>` : ""}${money(item.final_line_price)}</span>
          <button type="button" class="link-btn" data-line="${esc(item.key)}" data-qty="0">Entfernen</button>
        </div>
      </li>`;
  }

  function cartHtml() {
    if (!cart) return `<p class="cart-empty">Wird geladen …</p>`;
    if (!cart.items.length) {
      return `
        <div class="cart-empty">
          <p>Dein Warenkorb ist leer.</p>
          <a href="${esc(CFG.productUrl || ROOT)}" class="btn btn-primary">Zur Ampulle</a>
        </div>`;
    }
    const single = cart.items.find((i) => i.variant_id === CFG.singleVariantId);
    const oddSingle = OFFER.active && single && single.quantity % 3 === 2;
    return `
      ${oddSingle ? `
        <div class="cart-offer-hint">
          <span><b>Angebot 2 + 1 gratis:</b> Die dritte Flasche ist kostenlos.</span>
          <button type="button" class="link-btn" data-line="${esc(single.key)}" data-qty="${single.quantity + 1}">Gratis-Flasche hinzufügen</button>
        </div>` : ""}
      <ul class="cart-lines">${cart.items.map(lineHtml).join("")}</ul>
      <div class="cart-summary">
        <div class="cart-row"><span>Zwischensumme</span><span>${money(cart.original_total_price)}</span></div>
        ${cart.total_discount > 0 ? `<div class="cart-row cart-row--offer"><span>${esc(discountTitles())}</span><span>−${money(cart.total_discount)}</span></div>` : ""}
        <div class="cart-row"><span>Versand</span><span>Kostenlos</span></div>
        <div class="cart-row cart-row--total"><span>Gesamt</span><span>${money(cart.total_price)}</span></div>
        <p class="cart-legal">Endpreis · kein Ausweis der Umsatzsteuer gemäß Art. 293 B CGI (Kleinunternehmer) · Versand innerhalb von 2–3 Werktagen</p>
        <a href="${ROOT}checkout" class="btn btn-primary btn-block">Zur Kasse</a>
        <p class="cart-note">Sichere Zahlung im verschlüsselten Shopify-Checkout.</p>
      </div>`;
  }

  function renderCart() {
    const count = cart ? cart.item_count : 0;
    $$("[data-cart-count]").forEach((el) => { el.textContent = count; el.hidden = count === 0; });
    $$("[data-cart-view]").forEach((el) => { el.innerHTML = cartHtml(); });
  }

  document.addEventListener("click", (e) => {
    const btn = e.target.closest("[data-line]");
    if (btn) changeLine(btn.dataset.line, Number(btn.dataset.qty));
  });

  /* ---------- Cart drawer ---------- */
  const drawer = $("#cart-drawer");
  const backdrop = $(".drawer-backdrop");
  const onCartPage = Boolean($("[data-cart-page]"));
  let lastFocus = null;

  function openDrawer() {
    if (!drawer || onCartPage) return;
    lastFocus = document.activeElement;
    backdrop.hidden = false;
    drawer.setAttribute("aria-hidden", "false");
    requestAnimationFrame(() => { backdrop.classList.add("is-open"); drawer.classList.add("is-open"); });
    document.body.classList.add("drawer-open");
    drawer.focus();
  }

  function closeDrawer() {
    if (!drawer || !drawer.classList.contains("is-open")) return;
    drawer.classList.remove("is-open");
    backdrop.classList.remove("is-open");
    drawer.setAttribute("aria-hidden", "true");
    document.body.classList.remove("drawer-open");
    setTimeout(() => { backdrop.hidden = true; }, reducedMotion ? 0 : 400);
    if (lastFocus) lastFocus.focus();
  }

  $$("[data-cart-open]").forEach((el) => el.addEventListener("click", (e) => {
    if (onCartPage) return;
    e.preventDefault();
    openDrawer();
  }));
  $$("[data-cart-close]").forEach((el) => el.addEventListener("click", closeDrawer));
  window.addEventListener("keydown", (e) => { if (e.key === "Escape") closeDrawer(); });

  /* ---------- Buy boxes ---------- */
  const parseItems = (str) => str.split(",").map((pair) => {
    const [id, quantity] = pair.split(":");
    return { id: Number(id), quantity: Number(quantity) };
  });
  const visibleGroup = (box) => $(OFFER.active ? ".when-offer" : ".when-no-offer", box) || box;
  const selected = (box) => $("input[type=radio]:checked", visibleGroup(box)) || $("input[type=radio]", visibleGroup(box));

  const buyBoxes = $$("[data-buy-box]");
  function syncSticky() {
    const box = buyBoxes[0];
    const input = box && selected(box);
    if (!input) return;
    $$("[data-sticky-label]").forEach((el) => { el.textContent = input.dataset.label; });
    $$("[data-sticky-price]").forEach((el) => { el.textContent = input.dataset.price; });
  }

  async function handleAdd(button, items) {
    const label = button.innerHTML;
    button.disabled = true;
    try {
      await addItems(items);
      openDrawer();
    } catch {
      button.innerHTML = "Bitte erneut versuchen";
      setTimeout(() => { button.innerHTML = label; }, 2500);
    } finally {
      button.disabled = false;
    }
  }

  buyBoxes.forEach((box) => {
    box.addEventListener("change", syncSticky);
    box.addEventListener("submit", (e) => {
      e.preventDefault();
      const input = selected(box);
      if (input) handleAdd($("[type=submit]", box), parseItems(input.dataset.items));
    });
  });
  $$("[data-sticky-add]").forEach((btn) => btn.addEventListener("click", () => buyBoxes[0]?.requestSubmit()));
  $$("[data-add-items]").forEach((btn) => btn.addEventListener("click", (e) => {
    e.preventDefault();
    handleAdd(btn, parseItems(OFFER.active && btn.dataset.addItemsOffer ? btn.dataset.addItemsOffer : btn.dataset.addItems));
  }));
  syncSticky();

  /* ---------- Sticky buy bar ---------- */
  const sticky = $("[data-sticky-buy]");
  const mainBuy = $("[data-buy-anchor]");
  if (sticky && mainBuy && "IntersectionObserver" in window) {
    new IntersectionObserver(([entry]) => {
      const past = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      sticky.classList.toggle("is-visible", past);
      sticky.setAttribute("aria-hidden", String(!past));
      sticky.inert = !past;
    }).observe(mainBuy);
  }

  /* ---------- Gallery ---------- */
  $$("[data-gallery]").forEach((gallery) => {
    const track = $(".gallery-track", gallery);
    const slides = $$(".gallery-slide", gallery);
    const thumbs = $$("[data-thumb]", gallery);
    if (!track || !slides.length) return;
    let index = 0;
    const go = (i) => {
      index = (i + slides.length) % slides.length;
      track.scrollTo({ left: slides[index].offsetLeft - track.offsetLeft, behavior: reducedMotion ? "auto" : "smooth" });
    };
    const mark = () => {
      index = Math.round(track.scrollLeft / track.clientWidth);
      thumbs.forEach((t, i) => t.setAttribute("aria-current", String(i === index)));
    };
    thumbs.forEach((t, i) => t.addEventListener("click", () => go(i)));
    $("[data-gallery-prev]", gallery)?.addEventListener("click", () => go(index - 1));
    $("[data-gallery-next]", gallery)?.addEventListener("click", () => go(index + 1));
    track.addEventListener("scroll", () => requestAnimationFrame(mark), { passive: true });
    mark();
  });

  /* ---------- Morning / evening toggle ---------- */
  $$("[data-toggle]").forEach((group) => {
    const buttons = $$("button[data-mode]", group);
    const scope = group.closest("[data-toggle-scope]") || document;
    buttons.forEach((btn) => btn.addEventListener("click", () => {
      buttons.forEach((b) => b.setAttribute("aria-selected", String(b === btn)));
      $$("[data-mode-panel]", scope).forEach((panel) => { panel.hidden = panel.dataset.modePanel !== btn.dataset.mode; });
    }));
  });

  /* ---------- FAQ topic tabs ---------- */
  $$(".faq-tabs").forEach((tabs) => {
    const buttons = $$("button", tabs);
    buttons.forEach((btn) => btn.addEventListener("click", () => {
      buttons.forEach((b) => b.setAttribute("aria-selected", String(b === btn)));
      $$(".faq-group").forEach((g) => { g.hidden = btn.dataset.cat !== "alle" && g.dataset.cat !== btn.dataset.cat; });
    }));
  });

  /* ---------- Hero video: play once unless reduced motion ---------- */
  $$("video[data-autoplay]").forEach((video) => {
    if (reducedMotion) { video.removeAttribute("autoplay"); video.pause(); return; }
    video.muted = true;
    video.play().catch(() => { /* autoplay blocked: the poster stays */ });
  });

  /* ---------- Order tracking: hand the number to 17TRACK ---------- */
  $$("[data-track-form]").forEach((form) => form.addEventListener("submit", (e) => {
    e.preventDefault();
    const num = $("input", form).value.replace(/\s+/g, "").trim();
    if (num) window.open(`https://t.17track.net/de#nums=${encodeURIComponent(num)}`, "_blank", "noopener");
  }));

  /* ---------- Reviews (data from the "Bewertungen" theme section) ---------- */
  const starString = (r) => "★".repeat(r) + "☆".repeat(5 - r);
  let reviews = [];
  try { reviews = JSON.parse($("#liora-reviews-data")?.textContent || "[]").filter((r) => r && r.text); } catch { reviews = []; }

  function reviewEl(r) {
    const fig = document.createElement("figure");
    fig.className = "review";
    fig.innerHTML = `<div class="stars" aria-label="${r.rating} von 5 Sternen">${starString(r.rating)}</div><blockquote></blockquote><figcaption></figcaption>`;
    $("blockquote", fig).textContent = r.text;
    const cap = $("figcaption", fig);
    cap.textContent = r.name;
    if (r.tester) {
      const tag = document.createElement("span");
      tag.className = "review-meta";
      tag.textContent = "Early Tester";
      cap.append(tag);
    }
    return fig;
  }

  $$("[data-review-list]").forEach((list) => {
    const limit = Number(list.dataset.limit) || reviews.length;
    list.replaceChildren(...reviews.slice(0, limit).map(reviewEl));
    list.hidden = reviews.length === 0;
  });
  $$("[data-review-empty]").forEach((el) => { el.hidden = reviews.length > 0; });
  $$("[data-review-hide-empty]").forEach((el) => { el.hidden = reviews.length === 0; });
  const featured = [...reviews].sort((a, b) => b.rating - a.rating)[0];
  $$("[data-review-feature]").forEach((slot) => {
    slot.hidden = !featured;
    if (featured) { const card = reviewEl(featured); card.classList.add("review--feature"); slot.replaceChildren(card); }
  });
  if (reviews.length) {
    const avg = reviews.reduce((s, r) => s + r.rating, 0) / reviews.length;
    const allTesters = reviews.every((r) => r.tester);
    const label = `${avg.toFixed(1).replace(".", ",")} von 5 · ${reviews.length} ${reviews.length === 1 ? "Bewertung" : "Bewertungen"}${allTesters ? " von Early Testerinnen" : ""}`;
    $$("[data-review-summary]").forEach((el) => {
      el.hidden = false;
      el.innerHTML = `<span class="stars" aria-hidden="true">${starString(Math.round(avg))}</span><span></span>`;
      el.lastElementChild.textContent = label;
    });
  }

  /* ---------- Reveal on scroll ---------- */
  const reveals = $$("[data-reveal]");
  if (reducedMotion || !("IntersectionObserver" in window)) {
    reveals.forEach((el) => el.classList.add("is-in"));
  } else {
    const io = new IntersectionObserver((entries) => entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add("is-in");
      io.unobserve(entry.target);
    }), { rootMargin: "0px 0px -8% 0px" });
    reveals.forEach((el) => io.observe(el));
  }

  // Back from checkout (bfcache) or another tab: reload the cart
  window.addEventListener("pageshow", (e) => { if (e.persisted) refreshCart(); });
  refreshCart();
})();
